import { ADMIN_CARD_STATUSES } from '@carcheck/shared';
import { z } from 'zod';

import type { StatusOption } from '../../../components/filters/StatusMultiSelect';

/**
 * URL filter schema for the payout-cards list (AUT-126). Mirrors the
 * backend `ListAdminCardsQuerySchema` — `status` is a multi-select over the
 * FULL 7-value `ADMIN_CARD_STATUSES` (`binding` / `active` / `expired` /
 * `unbound` / `abandoned` / `suspended` / `failed`), and `inspectorId` is an
 * exact-match UUID (the admin drills into one inspector's cards — this is
 * also how the inspector-detail Cards tab scopes the SAME list).
 *
 * Default `status` is `[]` (ALL) — unlike the payouts queue (which defaults
 * to the `failed` triage rows), cards is a MONITORING surface, not a triage
 * queue: the operator lands on the full reconciliation view. There is NO
 * `q` free-text filter and NO date filter — neither is in the backend query
 * schema. The dead `bound` status the original ticket mentioned does NOT
 * exist (the bound state is `active`); it could never be selected here.
 *
 * Every key has a `.catch` default so a malformed / absent query param
 * degrades safely instead of throwing — `useUrlFilters` parses the raw
 * query string straight through.
 */

/**
 * `status=active,suspended` → string[] of valid statuses. When the raw
 * param is absent OR present-but-empty, defaults to `[]` (the monitoring
 * default — show everything).
 */
const statusSchema = z
  .string()
  .optional()
  .catch(undefined)
  .transform((raw) => {
    if (raw === undefined || raw === '') return [] as (typeof ADMIN_CARD_STATUSES)[number][];
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is (typeof ADMIN_CARD_STATUSES)[number] =>
        (ADMIN_CARD_STATUSES as readonly string[]).includes(s),
      );
  });

const inspectorIdSchema = z.string().optional().catch(undefined);

export const cardFiltersSchema = z.object({
  status: statusSchema,
  inspectorId: inspectorIdSchema,
});

export type CardFilters = z.infer<typeof cardFiltersSchema>;

/** Status filter options (multi-select) — labels resolved via i18n keys. */
export const STATUS_OPTIONS: StatusOption[] = ADMIN_CARD_STATUSES.map((value) => ({
  value,
  labelKey: `cards.status.${value}`,
}));

/** True when any managed filter is active (drives the Clear-filters link). */
export const hasActiveFilters = (filters: CardFilters): boolean =>
  (filters.status?.length ?? 0) > 0 ||
  Boolean(filters.inspectorId && filters.inspectorId.length > 0);
